import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { formatINR } from "../utils/format";

export default function Budget() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/budget/campaigns").then((r) => setRows(r.data)); }, []);

  const statusTone = (s) => s === "overspend_risk" ? "critical" : s === "underspend_risk" ? "warning" : "success";

  const totals = rows.reduce(
    (acc, r) => ({
      spend: acc.spend + r.spend_mtd,
      forecast: acc.forecast + r.forecast_spend,
      budget: acc.budget + r.monthly_budget,
    }),
    { spend: 0, forecast: 0, budget: 0 }
  );

  return (
    <Layout title="Budget & Pacing" subtitle="Month-to-date spend and forecast">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-card border border-border rounded-sm p-5 kpi-border-primary">
          <div className="label-mono">Monthly Budget · All Campaigns</div>
          <div className="font-heading text-3xl font-light tabular mt-2">{formatINR(totals.budget, { compact: true })}</div>
        </div>
        <div className="bg-card border border-border rounded-sm p-5 kpi-border-success">
          <div className="label-mono">Spend MTD</div>
          <div className="font-heading text-3xl font-light tabular mt-2">{formatINR(totals.spend, { compact: true })}</div>
          <div className="text-xs text-muted-foreground mt-1">{totals.budget ? `${(totals.spend / totals.budget * 100).toFixed(1)}% used` : "—"}</div>
        </div>
        <div className={`bg-card border border-border rounded-sm p-5 ${totals.forecast > totals.budget * 1.1 ? "kpi-border-critical" : "kpi-border-warning"}`}>
          <div className="label-mono">Forecast End-of-Month</div>
          <div className="font-heading text-3xl font-light tabular mt-2">{formatINR(totals.forecast, { compact: true })}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {totals.budget ? (totals.forecast > totals.budget ? `${formatINR(totals.forecast - totals.budget, { compact: true })} over` : `${formatINR(totals.budget - totals.forecast, { compact: true })} under`) : "—"}
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="budget-table">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Client</th>
              <th className="text-right">Monthly Budget</th>
              <th className="text-right">Spend MTD</th>
              <th className="text-right">Pacing</th>
              <th className="text-right">Forecast</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.campaign_id}>
                <td className="font-medium truncate max-w-[250px]">{r.campaign_name}</td>
                <td className="text-muted-foreground">{r.client_name}</td>
                <td className="text-right">{formatINR(r.monthly_budget)}</td>
                <td className="text-right font-medium">{formatINR(r.spend_mtd)}</td>
                <td className="text-right">
                  <div className="inline-flex items-center gap-2">
                    <div className="w-24 h-1.5 bg-secondary rounded-sm overflow-hidden">
                      <div className={`h-full ${r.pacing_status === "overspend_risk" ? "bg-[hsl(var(--critical))]" : r.pacing_status === "underspend_risk" ? "bg-[hsl(var(--warning))]" : "bg-[hsl(var(--success))]"}`} style={{ width: `${Math.min(r.pct_used, 100)}%` }} />
                    </div>
                    <span className="text-xs tabular">{r.pct_used}%</span>
                  </div>
                </td>
                <td className="text-right">{formatINR(r.forecast_spend)}</td>
                <td>
                  <span className={`text-[11px] px-2 py-0.5 rounded-sm uppercase font-semibold tracking-wider border
                    ${statusTone(r.pacing_status) === "critical" ? "bg-[hsl(var(--critical))]/10 text-[hsl(var(--critical))] border-[hsl(var(--critical))]/30" :
                    statusTone(r.pacing_status) === "warning" ? "bg-[hsl(var(--warning))]/10 text-[hsl(38_92%_30%)] border-[hsl(var(--warning))]/30" :
                    "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30"}`}>
                    {r.pacing_status.replace("_", " ")}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
